import { DataTableOptions, DataType, UserPermission } from '@globaleris/ui.library'
import {
  DataTableColumn,
  GlobDataTable,
  RowCustomAction
} from '@globaleris/ui.library/build/components/globalerisDataTable'
import { makeStyles, Tooltip, useTheme } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoCalculoConcepto } from '../../../../../api'
import { InvoiceConceptModel } from '../../../../../api/Argentia/endpoints/invoices/model/invoices'
import { ProductConceptModel } from '../../../../../api/Argentia/model/contracts'
import { enumToSelectOptionArray } from '../../../../../helpers'
import { associateActionIcon } from '../../../../../helpers/dataTableActionIconHelper'
import { useConfirmationDialog } from '../../../../../hooks'
import { ActionIcons } from '../../../../../model'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { styles } from '../../../invoicesPage.styles'
import {
  InvoiceConceptFormData,
  InvoiceConceptFormDialog
} from '../invoiceConceptFormDialog/invoiceConceptFormDialog'

// ─── Types ────────────────────────────────────────────────────────────────────

export type InvoiceConceptRow = InvoiceConceptModel & { dirty?: boolean }

export const PERCENTAGE_CALC_TYPES = new Set<number>([
  EnumTipoCalculoConcepto.Porcentaje,
  EnumTipoCalculoConcepto.PorcentajeUso,
  EnumTipoCalculoConcepto.PorcentajeRegion,
  EnumTipoCalculoConcepto.PorcentajeFacturaFinal,
  EnumTipoCalculoConcepto.PorcentajeIEE,
  EnumTipoCalculoConcepto.PorcentajeIVA,
  EnumTipoCalculoConcepto.PorcentajeIVARefactura,
  EnumTipoCalculoConcepto.PorcentajeIEERefactura
])

// ─── Props ────────────────────────────────────────────────────────────────────

interface InvoiceConceptsTableProps {
  loading?: boolean
  concepts: InvoiceConceptRow[]
  conceptsList?: ProductConceptModel[]
  defaultStartDate?: Date
  defaultEndDate?: Date
  isPercentage: boolean
  title: string
  onAdd: (concept: Omit<InvoiceConceptModel, 'id'>) => void
  onEdit: (concept: InvoiceConceptRow) => void
  onDelete: (conceptId: number) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export const InvoiceConceptsTable: React.FC<InvoiceConceptsTableProps> = React.memo(
  ({
    loading = false,
    concepts,
    conceptsList = [],
    defaultStartDate,
    defaultEndDate,
    isPercentage,
    title,
    onAdd,
    onEdit,
    onDelete
  }) => {
    const [t] = useTranslation([
      'globalerisLibrary',
      'enums',
      'invoiceEditPage',
      'informationDialogs'
    ])
    const currentTheme = useTheme()
    const useStyles = makeStyles(styles(currentTheme))
    const classes = useStyles()
    const { getConfirmation, closeInformation } = useConfirmationDialog()

    const [formDialogOpen, setFormDialogOpen] = useState(false)
    const [formDialogMode, setFormDialogMode] = useState<'add' | 'edit'>('add')
    const [editingConcept, setEditingConcept] = useState<InvoiceConceptRow | null>(null)

    // #region Handlers

    const handleOpenAdd = () => {
      setEditingConcept(null)
      setFormDialogMode('add')
      setFormDialogOpen(true)
    }

    const handleOpenEdit = (row: InvoiceConceptRow) => {
      // Look up the clean source row to avoid spreading the HTML dirtyIcon field
      const cleanRow = concepts.find(c => c.conceptId === row.conceptId) ?? row
      setEditingConcept(cleanRow)
      setFormDialogMode('edit')
      setFormDialogOpen(true)
    }

    const handleOpenDelete = async (row: InvoiceConceptRow) => {
      await getConfirmation(ConfirmationDialogs.DELETE_INVOICE_CONCEPT).then(() => {
        closeInformation()
        onDelete(row.conceptId)
      })
    }

    const handleFormDialogClose = (formData?: InvoiceConceptFormData) => {
      if (formData) {
        if (editingConcept) {
          onEdit({ ...editingConcept, ...formData, dirty: true })
        } else {
          onAdd(formData as Omit<InvoiceConceptModel, 'id'>)
        }
      }
      setFormDialogOpen(false)
      setEditingConcept(null)
    }

    // #endregion

    // ─── Row actions ────────────────────────────────────────────────────────────

    const rowCustomActions = useMemo(
      (): RowCustomAction[] => [
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('invoiceEditPage:concepts.actions.edit'),
          onClick: handleOpenEdit,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('invoiceEditPage:concepts.actions.delete'),
          onClick: handleOpenDelete,
          userPermission: UserPermission.EDIT
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      ],
      []
    )

    // ─── Columns ────────────────────────────────────────────────────────────────

    const regularColumns = useMemo(
      (): DataTableColumn[] => [
        { name: 'conceptId', type: DataType.NUMBER, display: false },
        { name: 'repeatedIcon', label: '', type: DataType.HTML, columnWidth: 28 },
        { name: 'dirtyIcon', label: '', type: DataType.HTML, columnWidth: 48 },
        {
          name: 'conceptCode',
          label: t('invoiceEditPage:concepts.columns.code'),
          type: DataType.TEXT
        },
        {
          name: 'conceptDetail',
          label: t('invoiceEditPage:concepts.columns.conceptDetail'),
          type: DataType.TEXT
        },
        {
          name: 'calculationType',
          label: t('invoiceEditPage:concepts.columns.calculationType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTipoCalculoConcepto,
            'enums:EnumTipoCalculoConcepto'
          )
        },
        {
          name: 'startDate',
          label: t('invoiceEditPage:concepts.columns.startDate'),
          type: DataType.DATE
        },
        {
          name: 'endDate',
          label: t('invoiceEditPage:concepts.columns.endDate'),
          type: DataType.DATE
        },
        {
          name: 'quantity',
          label: t('invoiceEditPage:concepts.columns.quantity'),
          type: DataType.NUMBER
        },
        {
          name: 'price',
          label: t('invoiceEditPage:concepts.columns.price'),
          type: DataType.NUMBER,
          decimalScale: 12
        },
        {
          name: 'amount',
          label: t('invoiceEditPage:concepts.columns.amount'),
          type: DataType.NUMBER,
          decimalScale: 12
        }
      ],
      [t]
    )

    const percentageColumns = useMemo(
      (): DataTableColumn[] => [
        { name: 'conceptId', type: DataType.NUMBER, display: false },
        { name: 'repeatedIcon', label: '', type: DataType.HTML, columnWidth: 28 },
        { name: 'dirtyIcon', label: '', type: DataType.HTML, columnWidth: 48 },
        {
          name: 'conceptCode',
          label: t('invoiceEditPage:concepts.columns.code'),
          type: DataType.TEXT
        },
        {
          name: 'conceptDetail',
          label: t('invoiceEditPage:concepts.columns.conceptDetail'),
          type: DataType.TEXT
        },
        {
          name: 'calculationType',
          label: t('invoiceEditPage:concepts.columns.calculationType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTipoCalculoConcepto,
            'enums:EnumTipoCalculoConcepto'
          )
        },
        {
          name: 'startDate',
          label: t('invoiceEditPage:concepts.columns.startDate'),
          type: DataType.DATE
        },
        {
          name: 'endDate',
          label: t('invoiceEditPage:concepts.columns.endDate'),
          type: DataType.DATE
        },
        { name: 'base', label: t('invoiceEditPage:concepts.columns.base'), type: DataType.NUMBER },
        {
          name: 'percentage',
          label: t('invoiceEditPage:concepts.columns.percentage'),
          type: DataType.NUMBER,
          decimalScale: 12
        },
        {
          name: 'amount',
          label: t('invoiceEditPage:concepts.columns.amount'),
          type: DataType.NUMBER
        }
      ],
      [t]
    )

    const columns = isPercentage ? percentageColumns : regularColumns

    // ─── Options ────────────────────────────────────────────────────────────────

    const options: DataTableOptions = useMemo(
      () => ({
        elevation: 2,
        search: true,
        pagination: true,
        rowsPerPage: 10,
        rowsPerPageOptions: [10, 25, 50],
        selectableRows: false,
        rowCustomActions,
        canInsert: true,
        customInsertEditModal: true,
        onRowInsert: () => {
          handleOpenAdd()
          return Promise.resolve(true)
        }
      }),
      [rowCustomActions]
    )

    // ─── Data ───────────────────────────────────────────────────────────────────

    const repeatedCodes = useMemo(() => {
      const counts: Record<string, number> = {}
      concepts.forEach(c => {
        if (c.conceptCode) counts[c.conceptCode] = (counts[c.conceptCode] ?? 0) + 1
      })
      return new Set(Object.keys(counts).filter(code => counts[code] > 1))
    }, [concepts])

    const tableData = useMemo(
      () =>
        concepts.map(c => ({
          ...c,
          dirtyIcon: c.dirty
            ? associateActionIcon(c, ActionIcons.NEW_ITEM, t('invoiceEditPage:concepts.unsaved'))
            : null,
          repeatedIcon: repeatedCodes.has(c.conceptCode) ? (
            <Tooltip
              title={t('invoiceEditPage:concepts.repeated', 'Concepto repetido. Validar fechas')}
              placement="top"
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#ffb300',
                  boxShadow: '0 0 6px 3px rgba(255, 179, 0, 0.55)'
                }}
              />
            </Tooltip>
          ) : null
        })),
      [concepts, repeatedCodes, t]
    )

    // ─── Render ─────────────────────────────────────────────────────────────────

    return (
      <>
        <GlobDataTable
          title={title}
          data={loading ? undefined : tableData}
          columns={columns}
          options={options}
          rowIdField="conceptId"
          className={classes.table}
        />

        {/* ── Add / Edit dialog ──────────────────────────────────────────── */}
        <InvoiceConceptFormDialog
          open={formDialogOpen}
          mode={formDialogMode}
          isPercentage={isPercentage}
          initialData={editingConcept ?? undefined}
          conceptsList={conceptsList}
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
          onClose={handleFormDialogClose}
        />
      </>
    )
  }
)
